import { createSelector } from "@ngrx/store";
import { QuoteStatus } from "src/api-service";
import { selectQuoteState } from "../quote.selectors";
import { selectVersion } from "../version/version.selectors";
import {
    QuoteValidation,
    QuoteLevelValidation,
    VersionLevelValidation,
    ValidationError,
    emptyQuoteValidation,
    emptyQuoteLevelValidation,
    emptyVersionLevelValidation,
} from "./validation.types";

// ============================================================================
// BASE SELECTORS
// ============================================================================

export const selectQuoteValidation = createSelector(
    selectQuoteState,
    (quote): QuoteValidation => quote?.validation ?? emptyQuoteValidation
);

export const selectQuoteLevelValidation = createSelector(
    selectQuoteValidation,
    (validation): QuoteLevelValidation => validation.quoteValidation ?? emptyQuoteLevelValidation
);

export const selectVersionLevelValidation = createSelector(
    selectQuoteValidation,
    (validation): VersionLevelValidation => validation.versionValidation ?? emptyVersionLevelValidation
);

// ============================================================================
// ERROR COUNTS
// ============================================================================

export const selectTotalQuoteErrors = createSelector(
    selectQuoteValidation,
    (validation): number => validation.totalQuoteErrors
);

export const selectTotalVersionErrors = createSelector(
    selectQuoteValidation,
    (validation): number => validation.totalVersionErrors
);

export const selectValidationErrorCount = createSelector(
    selectQuoteValidation,
    (validation): number => validation.totalOrderErrorCount
);

export const selectIsValidForOrder = createSelector(
    selectQuoteValidation,
    (validation): boolean => validation.isValidForOrder
);

export const selectSelectedVersionStatus = createSelector(
    selectVersion,
    (version): QuoteStatus => version?.status ?? QuoteStatus.Archived
);

// ============================================================================
// QUOTE-LEVEL ERRORS
// ============================================================================

export const selectHasCustomerErrors = createSelector(
    selectQuoteLevelValidation,
    (v): boolean => v.customerErrors.length > 0
);

export const selectHasQuoteErrors = createSelector(
    selectQuoteLevelValidation,
    (v): boolean => v.quoteErrors.length > 0
);

export const selectHasDeliveryErrors = createSelector(
    selectQuoteLevelValidation,
    (v): boolean => v.deliveryErrors.length > 0
);

export const selectHasSalesSupportErrors = createSelector(
    selectQuoteLevelValidation,
    (v): boolean => v.salesSupportErrors.length > 0
);

export const selectSalesSupportErrors = createSelector(
    selectQuoteLevelValidation,
    (v): ValidationError[] => v.salesSupportErrors
);

// ============================================================================
// VERSION-LEVEL ERRORS
// ============================================================================

export const selectHasShippingErrors = createSelector(
    selectVersionLevelValidation,
    (v): boolean => v.shippingErrors.length > 0
);

export const selectHasProjectErrors = createSelector(
    selectVersionLevelValidation,
    (v): boolean => v.projectErrors.length > 0
);

export const selectHasItemGroupErrors = createSelector(
    selectVersionLevelValidation,
    (v): boolean => v.itemGroupErrors.length > 0
);

export const selectHasItemErrors = createSelector(
    selectVersionLevelValidation,
    (v): boolean => v.itemErrors.length > 0
);

export const selectHasSubscriptionErrors = createSelector(
    selectVersionLevelValidation,
    (v): boolean => v.subscriptionErrors.length > 0
);

export const selectSubscriptionLockKey = createSelector(
    selectVersionLevelValidation,
    (v): string | null => v.subscriptionErrors[0]?.key ?? null
);

// ============================================================================
// COMBINED (for overview cards)
// ============================================================================

export const selectHasBasicInfoErrors = createSelector(
    selectHasQuoteErrors,
    (quoteErrors): boolean => quoteErrors
);

export const selectHasArticleErrors = createSelector(
    selectHasItemErrors,
    selectHasItemGroupErrors,
    (itemErrors, groupErrors): boolean => itemErrors || groupErrors
);

// ============================================================================
// DISPLAY CONFIG
// ============================================================================

export interface ValidationDisplayConfig {
    errorCount: number;
    warningCount: number;
    incompleteIcon: string;
    incompleteColor: string;
    incompleteTooltipKey: string;
    completeTooltipKey: string;
}

export const selectValidationDisplayConfig = createSelector(
    selectQuoteValidation,
    selectSelectedVersionStatus,
    (validation, status): ValidationDisplayConfig => {
        const isOrderMode = status === QuoteStatus.Sent || status === QuoteStatus.UnderReview;

        const tooltipKeys = {
            incompleteTooltipKey: 'QUOTE.DETAIL.OVERVIEW_NEW.STATUS_TOOLTIP_INCOMPLETE',
            completeTooltipKey: 'QUOTE.DETAIL.OVERVIEW_NEW.STATUS_TOOLTIP_OK',
        };

        if (isOrderMode) {
            return {
                errorCount: validation.totalOrderErrorCount,
                warningCount: validation.totalWarningCount,
                incompleteIcon: 'pi pi-exclamation-circle',
                incompleteColor: 'text-red-500',
                ...tooltipKeys,
            };
        }

        const vv = validation.versionValidation;
        const criticalErrorCount =
            vv.itemGroupErrors.filter(e => e.key.includes('DRAFT')).length +
            vv.itemErrors.filter(e => e.key.includes('DRAFT')).length +
            vv.subscriptionErrors.length;

        return {
            errorCount: criticalErrorCount,
            warningCount: (validation.totalOrderErrorCount - criticalErrorCount) + validation.totalWarningCount,
            incompleteIcon: 'pi pi-exclamation-triangle',
            incompleteColor: 'text-orange-500',
            ...tooltipKeys,
        };
    }
);
